'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Globe, Settings, ShieldCheck, Flag, CreditCard, Building2, Code2, Bell, FileText, Activity, ChevronLeft, ChevronRight, LogOut, Moon, Sun, Package, Lock, Zap, Users, Server, Paintbrush, Wrench, BarChart3, GitBranch, KeyRound, BookOpen, Send, Layers } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: string;
  badgeVariant?: 'danger' | 'warning' | 'info';
  group?: string;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Control Dashboard', icon: LayoutDashboard, href: '/', group: 'Overview' },
  { id: 'nav-platform-status', label: 'Platform Status', icon: Activity, href: '/platform-status', group: 'Overview' },
  { id: 'nav-system-health', label: 'System Health', icon: Zap, href: '/system-health', group: 'Overview' },
  { id: 'nav-global-config', label: 'Global Configuration', icon: Settings, href: '/global-config', group: 'Configuration' },
  { id: 'nav-auth-config', label: 'Auth Configuration', icon: Lock, href: '/auth-config', group: 'Configuration' },
  { id: 'nav-integration', label: 'Integration', icon: Layers, href: '/integration', group: 'Configuration' },
  { id: 'nav-feature-flags', label: 'Feature Flags', icon: Flag, href: '/feature-flags', badge: '3', badgeVariant: 'warning', group: 'Configuration' },
  { id: 'nav-module-mgmt', label: 'Module Management', icon: Package, href: '/modules', group: 'Configuration' },
  { id: 'nav-module-library', label: 'Module Library', icon: BookOpen, href: '/module-library', group: 'Configuration' },
  { id: 'nav-ui-config', label: 'Login Experience', icon: Globe, href: '/login-experience', group: 'Configuration' },
  { id: 'nav-global-branding', label: 'Global Branding', icon: Paintbrush, href: '/global-branding', group: 'Configuration' },
  { id: 'nav-security', label: 'Security Policies', icon: ShieldCheck, href: '/security', group: 'Security' },
  { id: 'nav-subscriptions', label: 'Subscription Plans', icon: CreditCard, href: '/subscriptions', group: 'Billing' },
  { id: 'nav-organizations', label: 'Organizations', icon: Building2, href: '/organizations', badge: '2', badgeVariant: 'info', group: 'Platform' },
  { id: 'nav-developer-accounts', label: 'Developer Accounts', icon: Code2, href: '/developer-accounts', group: 'Platform' },
  { id: 'nav-users', label: 'Platform Users', icon: Users, href: '/platform-users', group: 'Platform' },
  { id: 'nav-workshop-cms', label: 'Workshop CMS', icon: Wrench, href: '/workshop-cms', group: 'Modules' },
  { id: 'nav-notifications', label: 'Global Notifications', icon: Bell, href: '/notifications', badge: '5', badgeVariant: 'danger', group: 'Operations' },
  { id: 'nav-infrastructure', label: 'API Configuration', icon: Server, href: '/infrastructure', group: 'Operations' },
  { id: 'nav-audit', label: 'Audit Logs', icon: FileText, href: '/audit-logs', group: 'Operations' },
];

const groups = ['Overview', 'Configuration', 'Security', 'Billing', 'Platform', 'Modules', 'Operations'];

interface ControlSidebarProps {
  isDark: boolean;
  onToggleDark: () => void;
}

export default function ControlSidebar({ isDark, onToggleDark }: ControlSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [githubLogin, setGithubLogin] = useState('');
  const pathname = usePathname();

  useEffect(() => {
    let active = true;

    fetch('/api/control/session', {
      credentials: 'include',
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load GitHub identity');
        return response.json();
      })
      .then((data) => {
        if (active && typeof data.login === 'string') {
          setGithubLogin(data.login);
        }
      })
      .catch(() => {
        // Keep the label empty when the identity endpoint is unavailable.
      });

    return () => {
      active = false;
    };
  }, []);

  const badgeClass = (variant?: string) => {
    if (variant === 'danger') return 'bg-danger text-white';
    if (variant === 'warning') return 'bg-warning text-white';
    if (variant === 'info') return 'bg-info text-white';
    return 'bg-primary text-primary-foreground';
  };

  const handleLogout = () => {
    localStorage.removeItem('aac-control-token');
    localStorage.removeItem('aac-control-user');
    window.location.href = '/auth/github/logout';
  };

  return (
    <aside
      className={`
        sidebar-transition flex flex-col h-screen bg-card border-r border-border
        ${collapsed ? 'w-[68px] min-w-[68px]' : 'w-[260px] min-w-[260px]'}
        relative z-30 shrink-0
      `}
    >
      <div className={`flex items-center h-[64px] border-b border-border px-4 ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <AppLogo src="/assets/images/AAC_Logo-1787938885834.jpg" size={36} />
        {!collapsed && (
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-sm text-foreground leading-tight truncate">API App Cloud</span>
            <span className="text-[10px] font-medium text-danger uppercase tracking-widest leading-tight">Control</span>
          </div>
        )}
      </div>

      {!collapsed && (
        <div className="mx-3 mt-3 px-3 py-2 rounded-lg bg-success/10 border border-success/25 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-success pulse-dot shrink-0" />
          <div className="min-w-0">
            <p className="text-[11px] font-600 text-success leading-tight truncate">GitHub Auth Active</p>
            <p className="text-[10px] text-muted-foreground truncate">
              {githubLogin ? `@${githubLogin}` : 'Loading...'}
            </p>
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto scrollbar-thin py-2 px-2 mt-1">
        {groups.map((group) => {
          const groupItems = navItems.filter(i => i.group === group);
          if (groupItems.length === 0) return null;
          return (
            <div key={`group-${group}`} className="mb-2">
              {!collapsed && (
                <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground px-2 pt-3 pb-1">
                  {group}
                </p>
              )}
              {collapsed && <div className="my-1 border-t border-border/50 mx-2" />}
              {groupItems.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.href;
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`
                      flex items-center gap-3 px-2 py-2 rounded-lg mb-0.5 transition-all duration-150
                      ${isActive
                        ? 'bg-primary/10 text-primary font-600' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }
                      ${collapsed ? 'justify-center' : ''}
                    `}
                  >
                    <Icon size={18} className="shrink-0" />
                    {!collapsed && (
                      <span className="text-sm truncate flex-1">{item.label}</span>
                    )}
                    {!collapsed && item.badge && (
                      <span className={`text-[10px] font-700 px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none ${badgeClass(item.badgeVariant)}`}>
                        {item.badge}
                      </span>
                    )}
                    {collapsed && item.badge && (
                      <span className={`absolute top-0.5 right-0.5 text-[9px] font-700 w-4 h-4 flex items-center justify-center rounded-full ${badgeClass(item.badgeVariant)}`}>
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-border p-2 space-y-1">
        <button
          onClick={onToggleDark}
          className={`flex items-center gap-3 w-full px-2 py-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          title={isDark ? 'Light Mode' : 'Dark Mode'}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
          {!collapsed && <span className="text-sm">{isDark ? 'Light Mode' : 'Dark Mode'}</span>}
        </button>
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 w-full px-2 py-2 rounded-lg text-danger hover:bg-danger/10 transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          title="Sign Out"
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm font-500">Sign Out</span>}
        </button>
      </div>

      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-[72px] w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center shadow-sm hover:bg-muted transition-colors z-40"
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}